import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, FormattedPlural } from 'react-intl';

import { trackEvent } from '@xingternal/hops-tracking';
import { IconCross } from '@xingternal/icons';

import { FullScreenModal } from '@/components/full-screen-modal';
import { TaskHeader } from '@/components/task-header';
import { DEFAULT_SORT, SORT_OPTIONS } from '@/domain/search/constants';
import { useSearchQuery } from '@/domain/search/contexts';
import {
  Aggregations,
  FilterResult,
  getFiltersFromAggregations,
  isFullRemoteOnlyByFilterCollection,
} from '@/domain/search/helpers';
import {
  InputMaybe,
  JobFilterSalary,
  JobSearchFilterCollectionSplitBenefitsFragment,
  JobSearchQueryBody,
  JobSearchQueryFilterInput,
  SmallScreenFiltersResultsQuery,
  useSmallScreenFiltersResultsLazyQuery,
} from '@/graphql/generated';
import { useLoginStates } from '@/hooks';

import { LimitedJobCount } from '../../../limited-job-count';
import { BottomSheetFilter, MultiTagFilter, SalaryFilter, TwoLevelFilter } from '../filters';
import {
  getRadiusOptions,
  isDefined,
  parseFilterCollectionToFilterQuery,
  parseFiltersToQuery,
  parserLocationQueryToLocationInput,
  typeToFilter,
} from './full-screen-filters-modal.helpers';

import * as S from './full-screen-filters-modal.styles';

type FilterCollection = Array<JobSearchFilterCollectionSplitBenefitsFragment | null>;

type Props = {
  initialTotal?: number;
  initialAggregations: Aggregations;
  initialFilterCollection: FilterCollection;
  searchKeywords: JobSearchQueryBody['keywords'];
  searchLocation: JobSearchQueryBody['location'];
  show: boolean;
  onClose: () => void;
  onSave: () => void;
};

const MULTI_TAG_FILTERS: Record<FilterResult['paramName'], boolean> = {
  remoteOption: true,
  benefit: false,
  employmentType: true,
  careerLevel: true,
  discipline: false,
  industry: false,
  country: false,
  city: false,
};

// This is required since current and previous can be undefined. If they are undefined
// it does not mean that there are not any results, rather than the query has not returned
// anything yet. Since the ?? operator matches both undefined and null, it is not useful
// since we need to take care of each case differently
const getCurrentFilterCollection = (
  current: SmallScreenFiltersResultsQuery | undefined,
  previous: SmallScreenFiltersResultsQuery | undefined,
  initialCollection: FilterCollection
): FilterCollection => {
  const currentCollection = current?.jobSearchByQuery?.searchQuery.body?.filterCollection;
  if (currentCollection !== undefined) {
    // We want to return empty array if there are no filters, instead of null
    return currentCollection ?? [];
  }

  const previousCollection = previous?.jobSearchByQuery?.searchQuery.body?.filterCollection;
  if (previousCollection !== undefined) {
    // We want to return empty array if there are no filters, instead of null
    return previousCollection ?? [];
  }

  return initialCollection;
};

export const FullScreenFiltersModal: React.VFC<Props> = ({
  initialTotal,
  initialAggregations,
  initialFilterCollection,
  searchKeywords,
  searchLocation,
  show,
  onClose,
  onSave,
}) => {
  const { isLoggedOut } = useLoginStates();
  const {
    queryParams: [{ sort, location }, setQueryParams],
    searchBox: [{ radiusInput }],
  } = useSearchQuery();
  const [internalSort, setInternalSort] = useState(sort ?? DEFAULT_SORT);
  const [internalRadius, setInternalRadius] = useState(radiusInput ?? 0);
  const [
    getSmallScreenFiltersResults,
    { data: resultsData, previousData: previousResults, loading: areResultsLoading },
  ] = useSmallScreenFiltersResultsLazyQuery({ errorPolicy: 'all' });

  const canFetch = useRef(true);

  const queryConsumer = `${isLoggedOut ? 'loggedout' : 'loggedin'}.web.search_filters.center`;
  const currentFilterCollection = getCurrentFilterCollection(resultsData, previousResults, initialFilterCollection);

  const getSearchResultsQuery = useCallback(
    (filters: InputMaybe<JobSearchQueryFilterInput>): void => {
      getSmallScreenFiltersResults({
        variables: {
          query: {
            filter: filters,
            keywords: searchKeywords,
            location: parserLocationQueryToLocationInput(searchLocation),
          },
          consumer: queryConsumer,
        },
      });
    },
    [searchKeywords, searchLocation, getSmallScreenFiltersResults, queryConsumer]
  );

  const fetchInitialFilters = (): void => {
    getSmallScreenFiltersResults({
      variables: {
        query: {
          keywords: searchKeywords,
          location: parserLocationQueryToLocationInput(searchLocation),
          filter: parseFilterCollectionToFilterQuery(initialFilterCollection),
        },
        consumer: queryConsumer,
      },
    });
  };

  const handleOnRadiusChange = (radius: string): void => {
    setInternalRadius(Number(radius));
  };

  const handleOnManyFilterClick =
    (paramName: FilterResult['paramName']) =>
    (filterId: string): void => {
      const parsed = parseFilterCollectionToFilterQuery(currentFilterCollection);
      if (!parsed) {
        getSearchResultsQuery({ [paramName]: { id: [filterId] } });
        return;
      }

      const currentParsed = parsed[paramName]?.id ?? [];
      // Check if the filter already existed
      const existsIndex = currentParsed.findIndex((id) => filterId === id);
      if (existsIndex > -1) {
        // In case it exists, has to be deleted
        // splice can be used since immutability is not important
        currentParsed.splice(existsIndex, 1);
      } else {
        // Oterwhise, add it
        currentParsed.push(filterId);
      }
      getSearchResultsQuery({ ...parsed, [paramName]: { id: currentParsed } });
    };

  const handleOnSalaryChange = (filterResult: [number, number] | undefined): void => {
    getSearchResultsQuery({
      ...parseFilterCollectionToFilterQuery(currentFilterCollection),
      salary: filterResult ? { min: filterResult[0], max: filterResult[1] } : undefined,
    });
  };

  const handleOnClose = (): void => {
    trackEvent('PropTrackAction', { PropTrackAction: 'jobs_search_filter_setting_cancel' });
    fetchInitialFilters();
    setInternalSort(sort ?? DEFAULT_SORT);
    setInternalRadius(radiusInput ?? 0);
    onClose();
  };

  const handleOnSave = (): void => {
    setQueryParams({
      ...parseFiltersToQuery(currentFilterCollection),
      sort: internalSort === DEFAULT_SORT ? undefined : internalSort,
      radius: internalRadius === 0 ? undefined : internalRadius,
      sc_o: 'jobs_search_filter_setting_confirm',
    });
    onSave();
  };

  const handleOnResetFilters = (): void => {
    trackEvent('PropTrackAction', { PropTrackAction: 'jobs_search_filter_setting_reset' });
    // Update aggregations as there isn't any filter selected
    getSmallScreenFiltersResults({
      variables: {
        query: {
          // Do not add filters
          keywords: searchKeywords,
          location: parserLocationQueryToLocationInput(searchLocation),
        },
        consumer: queryConsumer,
      },
    });
    setInternalSort(DEFAULT_SORT);
    setInternalRadius(0);
  };

  useEffect(() => {
    if (show && canFetch.current) {
      // Fetch on load
      fetchInitialFilters();
    }

    canFetch.current = !show;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    setInternalSort(sort ?? DEFAULT_SORT);
  }, [sort]);

  const total = resultsData?.jobSearchByQuery?.total;
  const previousTotal = previousResults?.jobSearchByQuery?.total;
  const anyTotal = total ?? previousTotal ?? initialTotal;
  const validAggregations =
    resultsData?.jobSearchByQuery?.aggregations ??
    previousResults?.jobSearchByQuery?.aggregations ??
    initialAggregations;
  const totalQuery = areResultsLoading ? (
    <FormattedMessage id="JOBS_SEARCH_FILTERS_LOADING" />
  ) : anyTotal !== undefined ? (
    anyTotal === 0 ? (
      <FormattedMessage id="JOBS_SEARCH_FILTERS_TOTAL_RESULTS.zero" />
    ) : (
      <FormattedPlural
        value={anyTotal}
        one={<FormattedMessage id="JOBS_SEARCH_FILTERS_TOTAL_RESULTS.one" values={{ count: 1 }} />}
        other={
          <FormattedMessage
            id="JOBS_SEARCH_FILTERS_TOTAL_RESULTS.other"
            values={{ count: <LimitedJobCount count={anyTotal} /> }}
          />
        }
      />
    )
  ) : undefined;

  return (
    <FullScreenModal show={show}>
      <TaskHeader
        title={<FormattedMessage id="JOBS_SEARCH_MORE_FILTERS_MOBILE" />}
        subtitle={totalQuery}
        onClose={handleOnClose}
        onSave={handleOnSave}
      />
      <S.ModalContent>
        <BottomSheetFilter
          header={<FormattedMessage id="JOB_SEARCH_SORT" />}
          options={SORT_OPTIONS}
          selected={internalSort}
          onOptionClick={setInternalSort}
        />
        {location ? (
          <BottomSheetFilter
            header={<FormattedMessage id="XJM_SEARCH_LOCATION_RADIUS" />}
            options={getRadiusOptions(location)}
            disabled={isFullRemoteOnlyByFilterCollection(currentFilterCollection)}
            selected={String(internalRadius ?? 0)}
            onOptionClick={handleOnRadiusChange}
          />
        ) : null}
        {getFiltersFromAggregations(validAggregations).map((filter) => {
          if (typeof filter !== 'string') {
            return MULTI_TAG_FILTERS[filter.paramName] ? (
              <MultiTagFilter
                key={filter.titleKey}
                filterTypename={typeToFilter[filter.paramName]}
                filter={filter}
                selectedFilters={currentFilterCollection}
                onFilterClick={handleOnManyFilterClick(filter.paramName)}
              />
            ) : (
              <TwoLevelFilter
                key={filter.titleKey}
                aggregations={filter.options ?? []}
                filter={filter}
                isLoading={areResultsLoading}
                selectedFilters={currentFilterCollection}
                onFilterClick={handleOnManyFilterClick(filter.paramName)}
              />
            );
          }

          if (filter === 'salary') {
            const salaryFilter = currentFilterCollection.find(
              (filter) => filter?.__typename === 'JobFilterSalary' && isDefined(filter.min) && isDefined(filter.max)
            ) as JobFilterSalary | undefined;

            const salary = salaryFilter
              ? // need the casting oterwise TS won't accept the tuple
                ([salaryFilter.min, salaryFilter.max] as [number, number])
              : undefined;
            return <SalaryFilter key="salary" salaryValues={salary} onFilterChanged={handleOnSalaryChange} />;
          }
        })}
        <S.ResetButton icon={IconCross} variant="tertiary" onClick={handleOnResetFilters}>
          <FormattedMessage id="JOBS_SEARCH_RESET_FILTERS_BUTTON" />
        </S.ResetButton>
      </S.ModalContent>
    </FullScreenModal>
  );
};
